import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lightbulb, MessageSquare, Heart, Plus, Loader2, Send, ChevronDown, ChevronUp, X } from "lucide-react";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * /tips — camper-to-camper advice feed.
 *
 * A dedicated page surfacing threads tagged 'Tips & Tricks' as feature cards.
 * Each card is expandable inline: tapping "See replies" pulls the thread's
 * full replies list from GET /community/threads/{id} and shows a compose box
 * for logged-in users. iter129 — first-party tips feature under an
 * intentionally simple UX (no separate collection, just a well-labelled
 * threads view + composer with the tag pre-selected).
 */

const TIP_TAG = "Tips & Tricks";
const TIP_PROMPTS = [
  "The one thing you always forget to pack…",
  "How to stop dew soaking your fly-sheet…",
  "Best pitch position for windy sites…",
  "A campfire cook you nail every time…",
  "Rainy-day activity for kids in the tent…",
  "Something that saved your trip last minute…",
];

export default function Tips() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/community/threads?tag=${encodeURIComponent(TIP_TAG)}`);
      setTips(Array.isArray(data) ? data : []);
    } catch { setTips([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreated = (newTip) => {
    setTips((prev) => [newTip, ...prev]);
    setShowNew(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafafa]" data-testid="tips-page">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-2">
              <Lightbulb className="w-3 h-3" /> Camper tips
            </div>
            <h1 className="font-display text-3xl sm:text-4xl leading-tight mb-2">Advice from campers, for campers.</h1>
            <p className="text-sm sm:text-base text-[#4a4a4a] max-w-2xl">
              The little things you only learn by doing. Share what worked for you,
              and reply to help someone else nail their first (or fiftieth) trip.
            </p>
          </div>
          <button
            onClick={() => user ? setShowNew(true) : nav("/login?next=/tips")}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#ff4500] hover:bg-[#e63e00] text-white text-xs mono uppercase tracking-wider font-bold shadow-[0_5px_0_0_#0e0e0e] hover:shadow-[0_2px_0_0_#0e0e0e] hover:translate-y-[3px] transition-all"
            data-testid="tips-add-btn"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} /> Share a tip
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-[#666] py-8">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading tips…
          </div>
        )}

        {!loading && tips.length === 0 && (
          <div className="border-2 border-dashed hairline-strong rounded-2xl p-10 text-center bg-white" data-testid="tips-empty">
            <Lightbulb className="w-8 h-8 mx-auto mb-3 text-[#ff4500]" strokeWidth={1.5} />
            <h3 className="font-display text-xl mb-2">No tips yet — be first.</h3>
            <p className="text-sm text-[#666] mb-4">Even a two-line tip helps other campers avoid the same mistake.</p>
            <button
              onClick={() => user ? setShowNew(true) : nav("/login?next=/tips")}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#ff4500] hover:bg-[#e63e00] text-white text-xs mono uppercase tracking-wider font-bold"
            >
              <Plus className="w-3.5 h-3.5" /> Share the first tip
            </button>
          </div>
        )}

        <div className="space-y-4" data-testid="tips-list">
          {tips.map((tip) => (
            <TipCard
              key={tip.thread_id}
              tip={tip}
              user={user}
              expanded={expandedId === tip.thread_id}
              onToggle={() => setExpandedId(expandedId === tip.thread_id ? null : tip.thread_id)}
            />
          ))}
        </div>
      </main>
      <Footer />

      {showNew && (
        <NewTipModal
          onClose={() => setShowNew(false)}
          onCreated={onCreated}
        />
      )}
    </div>
  );
}

function TipCard({ tip, user, expanded, onToggle }) {
  const [full, setFull] = useState(null);   // full thread with replies once expanded
  const [busy, setBusy] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [kudosBusy, setKudosBusy] = useState(false);
  const [kudos, setKudos] = useState(tip.kudos || 0);
  const [kudosMine, setKudosMine] = useState(!!(user && (tip.kudos_by || []).includes(user.user_id)));

  useEffect(() => {
    if (!expanded || full) return;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/community/threads/${tip.thread_id}`);
        setFull(data);
      } catch { toast.error("Could not load replies"); }
      finally { setBusy(false); }
    })();
  }, [expanded, full, tip.thread_id]);

  const reply = async () => {
    const body = replyText.trim();
    if (body.length < 2) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/community/threads/${tip.thread_id}/replies`, { body });
      setFull((f) => f ? { ...f, replies: [...(f.replies || []), data] } : f);
      setReplyText("");
      toast.success("Reply added");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const toggleKudos = async () => {
    if (!user) { toast.info("Sign in to react"); return; }
    setKudosBusy(true);
    try {
      const { data } = await api.post(`/community/threads/${tip.thread_id}/kudos`);
      setKudos(data.kudos ?? kudos);
      setKudosMine(!!data.liked);
    } catch { /* silent */ }
    finally { setKudosBusy(false); }
  };

  const replyCount = full?.replies?.length ?? tip.reply_count ?? 0;

  return (
    <article className="bg-white border-2 hairline-strong rounded-2xl overflow-hidden shadow-[0_4px_0_0_#0e0e0e]" data-testid={`tip-card-${tip.thread_id}`}>
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h3 className="font-display text-lg sm:text-xl leading-tight mb-1 text-[#0e0e0e]" data-testid={`tip-title-${tip.thread_id}`}>{tip.title}</h3>
            <div className="text-[11px] mono text-[#666]">
              <span className="text-[#ff4500] font-bold">{tip.user_name || "Anon"}</span>
              {" · "}{new Date(tip.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>
        <p className="text-sm sm:text-base leading-relaxed text-[#333] whitespace-pre-wrap mb-4">{tip.body}</p>

        <div className="flex items-center gap-3 pt-3 border-t hairline">
          <button
            onClick={toggleKudos}
            disabled={kudosBusy}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs mono uppercase tracking-wider transition-colors ${kudosMine ? "bg-[#ff4500] text-white" : "bg-[#f5f5f5] text-[#0e0e0e] hover:bg-[#fff4ee]"}`}
            data-testid={`tip-kudos-${tip.thread_id}`}
          >
            <Heart className={`w-3.5 h-3.5 ${kudosMine ? "fill-white" : ""}`} strokeWidth={2} />
            {kudos} helpful
          </button>
          <button
            onClick={onToggle}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs mono uppercase tracking-wider bg-[#f5f5f5] text-[#0e0e0e] hover:bg-[#fff4ee] transition-colors"
            data-testid={`tip-toggle-replies-${tip.thread_id}`}
          >
            <MessageSquare className="w-3.5 h-3.5" strokeWidth={2} />
            {replyCount} {replyCount === 1 ? "reply" : "replies"}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          <Link
            to={`/community/${tip.thread_id}`}
            className="ml-auto text-[11px] mono text-[#666] hover:text-[#ff4500] underline"
          >Open thread →</Link>
        </div>
      </div>

      {expanded && (
        <div className="border-t hairline bg-[#fafafa] p-5 sm:p-6" data-testid={`tip-replies-${tip.thread_id}`}>
          {busy && !full && <div className="text-xs text-[#666] flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>}
          {full && (
            <>
              {(full.replies || []).length === 0 && (
                <p className="text-xs text-[#888] mb-4">No replies yet — be the first to add a thought.</p>
              )}
              <ul className="space-y-3 mb-4">
                {(full.replies || []).map((r) => (
                  <li key={r.reply_id} className="bg-white border hairline rounded-xl p-3">
                    <div className="text-[11px] mono text-[#666] mb-1">
                      <span className="text-[#ff4500] font-bold">{r.user_name || "Anon"}</span>
                      {" · "}{new Date(r.created_at).toLocaleDateString()}
                    </div>
                    <p className="text-sm text-[#333] whitespace-pre-wrap leading-relaxed">{r.body}</p>
                  </li>
                ))}
              </ul>
              {user ? (
                <div className="flex items-end gap-2">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value.slice(0, 800))}
                    placeholder="Add your thought — polite, useful, one paragraph is plenty."
                    rows={2}
                    className="flex-1 border hairline rounded-xl px-3 py-2 text-sm bg-white resize-none focus:outline-none focus:border-[#ff4500]"
                    data-testid={`tip-reply-input-${tip.thread_id}`}
                  />
                  <button
                    onClick={reply}
                    disabled={busy || replyText.trim().length < 2}
                    className="p-3 rounded-xl bg-[#ff4500] hover:bg-[#e63e00] disabled:opacity-40 text-white"
                    data-testid={`tip-reply-send-${tip.thread_id}`}
                    aria-label="Send reply"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={2} />}
                  </button>
                </div>
              ) : (
                <p className="text-xs text-[#666]">
                  <Link to="/login?next=/tips" className="text-[#ff4500] underline">Sign in</Link> to add a reply.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </article>
  );
}

function NewTipModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const prompt = useMemo(() => TIP_PROMPTS[Math.floor(Math.random() * TIP_PROMPTS.length)], []);

  const submit = async () => {
    if (title.trim().length < 4) return toast.error("Give your tip a short title (4+ chars)");
    if (body.trim().length < 8) return toast.error("Add at least a sentence of body");
    setBusy(true);
    try {
      const { data } = await api.post("/community/threads", {
        title: title.trim(),
        body: body.trim(),
        tag: TIP_TAG,
      });
      toast.success("Tip shared · thanks 🙌");
      onCreated(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="new-tip-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white w-full sm:max-w-lg max-h-[95vh] overflow-y-auto border-2 hairline-strong shadow-[0_8px_0_0_#0e0e0e] rounded-t-2xl sm:rounded-2xl">
        <header className="sticky top-0 bg-white border-b hairline px-5 py-3 flex items-center justify-between">
          <h2 className="font-display text-xl inline-flex items-center gap-2"><Lightbulb className="w-4 h-4 text-[#ff4500]" /> Share a tip</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[#f5f5f5]" data-testid="new-tip-close" aria-label="Close">
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </header>
        <div className="px-5 py-4 space-y-4">
          <div className="bg-[#fff4ee] border border-[#ffb074] rounded-xl px-3 py-2 text-xs text-[#7a4f00]">
            <strong>Prompt idea:</strong> {prompt}
          </div>
          <div>
            <label className="eyebrow block mb-1.5">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 120))}
              placeholder="Short, honest — e.g. 'Peg direction matters more than you think'"
              className="w-full border hairline rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#ff4500]"
              data-testid="new-tip-title"
              autoFocus
            />
            <div className="text-[10px] mono text-[#888] text-right mt-1">{title.length}/120</div>
          </div>
          <div>
            <label className="eyebrow block mb-1.5">Your tip</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 2000))}
              placeholder="A paragraph or two — what happened, what you did, what worked. No self-promo please."
              rows={7}
              className="w-full border hairline rounded-xl px-3 py-2 text-sm leading-relaxed resize-y focus:outline-none focus:border-[#ff4500]"
              data-testid="new-tip-body"
            />
            <div className="text-[10px] mono text-[#888] text-right mt-1">{body.length}/2000</div>
          </div>
        </div>
        <footer className="sticky bottom-0 bg-white border-t hairline px-5 py-3 flex items-center justify-between gap-3">
          <button onClick={onClose} className="text-sm mono underline text-[#666]">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || title.trim().length < 4 || body.trim().length < 8}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#ff4500] hover:bg-[#e63e00] disabled:opacity-40 text-white text-xs mono uppercase tracking-wider font-bold"
            data-testid="new-tip-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={2} />}
            {busy ? "Sharing…" : "Post tip"}
          </button>
        </footer>
      </div>
    </div>
  );
}
